import React, { useState, useEffect } from 'react';
import {
  BuildingOfficeIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  InformationCircleIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  LinkIcon,
  ExclamationTriangleIcon,
  PlusCircleIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../services/api';
import { useTranslation } from 'react-i18next';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';

interface Country {
  name: string;
  regions_label: string;
  currency: string;
  currency_symbol: string;
}

interface BusinessType {
  name: string;
  short_name: string;
  description: string;
  pros: string[];
  cons: string[];
  best_for: string[];
}

interface Owner {
  name: string;
  ownership_percentage: number;
  role: string;
}

interface ChecklistItem {
  name: string;
  required: boolean;
  description: string;
  completed?: boolean;
  url?: string;
  state_url?: string;
}

interface FilingStep {
  step: number;
  title: string;
  description: string;
  estimated_time: string;
  cost: string;
  url?: string;
  link_text?: string;
}

interface GeneratedDocument {
  document_type: string;
  document_name: string;
  requires_additional_info: boolean;
  additional_fields: AdditionalField[];
  content: string | null;
  html_content: string | null;
  error?: string;
}

interface AdditionalField {
  name: string;
  label: string;
  type: string;
  required: boolean;
  default?: string | number;
}

interface JurisdictionLink {
  name: string;
  url: string;
  notes?: string;
}

const STEPS = [
  'selectJurisdiction',
  'selectBusinessType',
  'businessDetails',
  'ownerInfo',
  'reviewRequirements',
  'generateDocuments',
  'filingInstructions',
];

export const BusinessFormation: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Step management
  const [currentStep, setCurrentStep] = useState(0);

  // Selection state
  const [countries, setCountries] = useState<Record<string, Country>>({});
  const [selectedCountry, setSelectedCountry] = useState('');
  const [regions, setRegions] = useState<Record<string, string>>({});
  const [regionsLabel, setRegionsLabel] = useState('State');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [businessTypes, setBusinessTypes] = useState<Record<string, BusinessType>>({});
  const [selectedBusinessType, setSelectedBusinessType] = useState('');

  // Business details
  const [businessName, setBusinessName] = useState('');
  const [businessPurpose, setBusinessPurpose] = useState('');
  const [principalAddress, setPrincipalAddress] = useState('');
  const [registeredAgentName, setRegisteredAgentName] = useState('');
  const [registeredAgentAddress, setRegisteredAgentAddress] = useState('');
  const [managementType, setManagementType] = useState('member');
  const [fiscalYearEnd, setFiscalYearEnd] = useState('December 31');

  // Owners
  const [owners, setOwners] = useState<Owner[]>([{ name: '', ownership_percentage: 100, role: 'Member' }]);

  // Requirements and documents
  const [requirements, setRequirements] = useState<any>(null);
  const [checklist, setChecklist] = useState<Record<string, ChecklistItem[]>>({});
  const [generatedDocuments, setGeneratedDocuments] = useState<GeneratedDocument[]>([]);
  const [jurisdictionLinks, setJurisdictionLinks] = useState<JurisdictionLink[]>([]);
  const [filingInstructions, setFilingInstructions] = useState<any>(null);
  const [filingOffice, setFilingOffice] = useState('');
  const [stateWebsite, setStateWebsite] = useState('');

  // Document preview and additional fields modal
  const [selectedDocument, setSelectedDocument] = useState<GeneratedDocument | null>(null);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [showAdditionalFieldsModal, setShowAdditionalFieldsModal] = useState(false);
  const [additionalFieldsValues, setAdditionalFieldsValues] = useState<Record<string, any>>({});
  const [documentBeingGenerated, setDocumentBeingGenerated] = useState<string | null>(null);
  const [exportFormat, setExportFormat] = useState<'pdf' | 'docx' | 'txt'>('pdf');

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load countries on mount
  useEffect(() => {
    loadCountries();
  }, []);

  // Load regions when country changes
  useEffect(() => {
    if (selectedCountry) {
      loadRegions(selectedCountry);
      loadBusinessTypes(selectedCountry);
    }
  }, [selectedCountry]);

  const loadCountries = async () => {
    try {
      const response = await apiClient.get('/api/business-formation/countries');
      setCountries(response.data.countries);
    } catch (err) {
      console.error('Failed to load countries:', err);
    }
  };

  const loadRegions = async (country: string) => {
    try {
      const response = await apiClient.post('/api/business-formation/regions', {
        country,
        language: i18n.language,
      });
      setRegions(response.data.regions);
      setRegionsLabel(response.data.regions_label);
    } catch (err) {
      console.error('Failed to load regions:', err);
    }
  };

  const loadBusinessTypes = async (country: string) => {
    try {
      const response = await apiClient.post('/api/business-formation/business-types', {
        country,
        language: i18n.language,
      });
      setBusinessTypes(response.data.business_types);
    } catch (err) {
      console.error('Failed to load business types:', err);
    }
  };

  const loadRequirements = async () => {
    if (!selectedCountry || !selectedRegion || !selectedBusinessType) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/api/business-formation/requirements', {
        country: selectedCountry,
        region: selectedRegion,
        business_type: selectedBusinessType,
        language: i18n.language,
      });
      setRequirements(response.data);
      // Initialize checklist with completed status
      const checklistData = response.data.checklist || {};
      const initializedChecklist: Record<string, ChecklistItem[]> = {};
      Object.keys(checklistData).forEach((key) => {
        initializedChecklist[key] = checklistData[key].map((item: ChecklistItem) => ({
          ...item,
          completed: false,
        }));
      });
      setChecklist(initializedChecklist);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load requirements');
    } finally {
      setIsLoading(false);
    }
  };

  const generateDocuments = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/api/business-formation/generate-all-documents', {
        country: selectedCountry,
        region: selectedRegion,
        business_type: selectedBusinessType,
        business_name: businessName,
        business_purpose: businessPurpose,
        owners: owners.filter((o) => o.name.trim() !== ''),
        registered_agent_name: registeredAgentName,
        registered_agent_address: registeredAgentAddress,
        principal_address: principalAddress,
        management_type: managementType,
        fiscal_year_end: fiscalYearEnd,
        language: i18n.language,
      });
      setGeneratedDocuments(response.data.documents || []);
      setJurisdictionLinks(response.data.jurisdiction_links || []);
      setFilingOffice(response.data.filing_office || '');
      setStateWebsite(response.data.state_website || '');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to generate documents');
    } finally {
      setIsLoading(false);
    }
  };

  const generateSingleDocument = async (documentType: string, additionalFields?: Record<string, any>) => {
    setDocumentBeingGenerated(documentType);
    setError(null);
    try {
      const response = await apiClient.post('/api/business-formation/generate-single-document', {
        country: selectedCountry,
        region: selectedRegion,
        business_type: selectedBusinessType,
        document_type: documentType,
        business_name: businessName,
        business_purpose: businessPurpose,
        owners: owners.filter((o) => o.name.trim() !== ''),
        registered_agent_name: registeredAgentName,
        registered_agent_address: registeredAgentAddress,
        principal_address: principalAddress,
        management_type: managementType,
        fiscal_year_end: fiscalYearEnd,
        additional_fields: additionalFields,
        language: i18n.language,
      });

      // Update the document in the list
      setGeneratedDocuments((prev) =>
        prev.map((doc) =>
          doc.document_type === documentType
            ? {
                ...doc,
                content: response.data.content,
                html_content: response.data.html_content,
                requires_additional_info: false,
              }
            : doc
        )
      );

      setShowAdditionalFieldsModal(false);
      setAdditionalFieldsValues({});
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to generate document');
    } finally {
      setDocumentBeingGenerated(null);
    }
  };

  const handleExportDocument = async (doc: GeneratedDocument, format: 'pdf' | 'docx' | 'txt') => {
    if (!doc.content) return;

    try {
      const response = await apiClient.post(
        '/api/business-formation/export-document',
        {
          content: doc.content,
          title: `${businessName} - ${doc.document_name}`,
          format: format,
        },
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${businessName.replace(/\s+/g, '_')}_${doc.document_type}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to export document');
    }
  };

  const handleDownloadPackage = async () => {
    const docsToExport = generatedDocuments
      .filter((doc) => doc.content)
      .map((doc) => ({
        title: `${businessName} - ${doc.document_name}`,
        content: doc.content || '',
      }));

    if (docsToExport.length === 0) {
      setError('No documents available to download');
      return;
    }

    try {
      const response = await apiClient.post(
        '/api/business-formation/download-package',
        {
          documents: docsToExport,
          format: exportFormat,
          package_name: `${businessName.replace(/\s+/g, '_')}_Formation_Documents`,
        },
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${businessName.replace(/\s+/g, '_')}_Formation_Documents.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to download package');
    }
  };

  const handleOpenAdditionalFieldsModal = (doc: GeneratedDocument) => {
    // Initialize with default values
    const defaults: Record<string, any> = {};
    doc.additional_fields.forEach((field) => {
      defaults[field.name] = field.default || '';
    });
    setAdditionalFieldsValues(defaults);
    setSelectedDocument(doc);
    setShowAdditionalFieldsModal(true);
  };

  const loadFilingInstructions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/api/business-formation/filing-instructions', {
        country: selectedCountry,
        region: selectedRegion,
        business_type: selectedBusinessType,
        language: i18n.language,
      });
      setFilingInstructions(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load filing instructions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextStep = async () => {
    // Validation for each step
    if (currentStep === 0) {
      if (!selectedCountry) {
        setError(t('businessFormation:errors.selectCountry', 'Please select a country'));
        return;
      }
      if (!selectedRegion) {
        setError(t('businessFormation:errors.selectRegion', 'Please select a state/region'));
        return;
      }
    }
    if (currentStep === 1 && !selectedBusinessType) {
      setError(t('businessFormation:errors.selectBusinessType', 'Please select a business type'));
      return;
    }
    if (currentStep === 2) {
      if (!businessName.trim()) {
        setError(t('businessFormation:errors.enterBusinessName', 'Please enter a business name'));
        return;
      }
      if (!businessPurpose.trim()) {
        setError(t('businessFormation:errors.enterBusinessPurpose', 'Please enter a business purpose'));
        return;
      }
      if (!principalAddress.trim()) {
        setError(t('businessFormation:errors.enterAddress', 'Please enter a principal address'));
        return;
      }
    }
    if (currentStep === 3) {
      const validOwners = owners.filter((o) => o.name.trim() !== '');
      if (validOwners.length === 0) {
        setError(t('businessFormation:errors.addOwner', 'Please add at least one owner'));
        return;
      }
      const totalOwnership = validOwners.reduce((sum, o) => sum + o.ownership_percentage, 0);
      if (totalOwnership !== 100) {
        setError(t('businessFormation:errors.ownershipTotal', 'Total ownership must equal 100%'));
        return;
      }
    }

    setError(null);

    // Load data for upcoming steps
    if (currentStep === 3) {
      await loadRequirements();
    }
    if (currentStep === 4) {
      await generateDocuments();
    }
    if (currentStep === 5) {
      await loadFilingInstructions();
    }

    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  const addOwner = () => {
    setOwners([...owners, { name: '', ownership_percentage: 0, role: 'Member' }]);
  };

  const removeOwner = (index: number) => {
    if (owners.length > 1) {
      setOwners(owners.filter((_, i) => i !== index));
    }
  };

  const updateOwner = (index: number, field: keyof Owner, value: string | number) => {
    const newOwners = [...owners];
    newOwners[index] = { ...newOwners[index], [field]: value };
    setOwners(newOwners);
  };

  const toggleChecklistItem = (category: string, index: number) => {
    const newChecklist = { ...checklist };
    newChecklist[category][index].completed = !newChecklist[category][index].completed;
    setChecklist(newChecklist);
  };

  const resetForm = () => {
    // Reset to first step
    setCurrentStep(0);

    // Reset jurisdiction selection
    setSelectedCountry('');
    setSelectedRegion('');
    setRegions({});
    setRegionsLabel('State');

    // Reset business type
    setSelectedBusinessType('');
    setBusinessTypes({});

    // Reset business details
    setBusinessName('');
    setBusinessPurpose('');
    setPrincipalAddress('');
    setRegisteredAgentName('');
    setRegisteredAgentAddress('');
    setManagementType('member');
    setFiscalYearEnd('December 31');

    // Reset owners
    setOwners([{ name: '', ownership_percentage: 100, role: 'Member' }]);

    // Reset requirements and documents
    setRequirements(null);
    setChecklist({});
    setGeneratedDocuments([]);
    setJurisdictionLinks([]);
    setFilingInstructions(null);
    setFilingOffice('');
    setStateWebsite('');

    // Reset modal states
    setSelectedDocument(null);
    setShowDocumentPreview(false);
    setShowAdditionalFieldsModal(false);
    setAdditionalFieldsValues({});
    setDocumentBeingGenerated(null);
    setExportFormat('pdf');

    // Clear any errors
    setError(null);
  };

  const handlePrint = () => {
    if (selectedDocument?.html_content) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(selectedDocument.html_content);
        printWindow.document.close();
        printWindow.print();
      }
    } else {
      window.print();
    }
  };

  // Render step indicator
  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-center">
        {STEPS.map((step, index) => (
          <React.Fragment key={step}>
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                index < currentStep
                  ? 'bg-emerald-600 text-white'
                  : index === currentStep
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}
            >
              {index < currentStep ? <CheckIcon className="h-5 w-5" /> : index + 1}
            </div>
            {index < STEPS.length - 1 && (
              <div
                className={`w-12 h-1 mx-1 transition-colors ${
                  index < currentStep ? 'bg-emerald-600' : 'bg-gray-200 dark:bg-gray-700'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="text-center mt-2 text-sm text-gray-600 dark:text-gray-400">
        {t(`businessFormation:steps.${STEPS[currentStep]}`, STEPS[currentStep])}
      </div>
    </div>
  );

  // Step 0: Select Jurisdiction (Country + Region in one step like TenantRights)
  const renderJurisdictionSelection = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('businessFormation:selectCountry.title', 'Select Country')}
          </label>
          <select
            value={selectedCountry}
            onChange={(e) => {
              setSelectedCountry(e.target.value);
              setSelectedRegion('');
              setSelectedBusinessType('');
            }}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">{t('businessFormation:selectCountry.placeholder', 'Select a country...')}</option>
            {Object.entries(countries)
              .sort(([, a], [, b]) => a.name.localeCompare(b.name))
              .map(([code, country]) => (
                <option key={code} value={code}>
                  {country.name}
                </option>
              ))}
          </select>
        </div>

        {selectedCountry && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('businessFormation:selectRegion.title', `Select ${regionsLabel}`)}
            </label>
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">{t('businessFormation:selectRegion.placeholder', `Select a ${regionsLabel.toLowerCase()}...`)}</option>
              {Object.entries(regions)
                .sort(([, a], [, b]) => a.localeCompare(b))
                .map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
            </select>
          </div>
        )}
      </div>

      {selectedCountry && selectedRegion && (
        <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
            <span className="text-green-800 dark:text-green-200">
              {t('businessFormation:selectRegion.selectedLocation', 'Selected Location')}: {regions[selectedRegion]}, {countries[selectedCountry]?.name}
            </span>
          </div>
        </div>
      )}

      {selectedCountry === 'US' && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-start">
            <InformationCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-blue-800 dark:text-blue-200">
                {t('businessFormation:selectRegion.popularStates', 'Popular States for Business Formation')}
              </h4>
              <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                {t(
                  'businessFormation:selectRegion.popularStatesDesc',
                  'Delaware, Wyoming, and Nevada are popular choices due to business-friendly laws and tax benefits. However, forming in your home state is often simpler and more cost-effective.'
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Step 2: Select Business Type
  const renderBusinessTypeSelection = () => (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
        {t('businessFormation:selectBusinessType.title', 'Select Business Type')}
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        {t(
          'businessFormation:selectBusinessType.description',
          'Choose the type of business entity you want to form.'
        )}
      </p>
      <div className="space-y-4">
        {Object.entries(businessTypes).map(([code, type]) => (
          <button
            key={code}
            onClick={() => setSelectedBusinessType(code)}
            className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
              selectedBusinessType === code
                ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-700'
            }`}
          >
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{type.name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{type.description}</p>
              </div>
              {selectedBusinessType === code && (
                <CheckCircleIcon className="h-6 w-6 text-emerald-500 flex-shrink-0" />
              )}
            </div>

            {selectedBusinessType === code && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-green-700 dark:text-green-400 mb-2">
                    {t('businessFormation:pros', 'Pros')}
                  </h4>
                  <ul className="text-sm space-y-1">
                    {type.pros.map((pro, idx) => (
                      <li key={idx} className="flex items-start text-gray-600 dark:text-gray-400">
                        <CheckIcon className="h-4 w-4 text-green-500 mr-1 mt-0.5 flex-shrink-0" />
                        {pro}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-red-700 dark:text-red-400 mb-2">
                    {t('businessFormation:cons', 'Cons')}
                  </h4>
                  <ul className="text-sm space-y-1">
                    {type.cons.map((con, idx) => (
                      <li key={idx} className="flex items-start text-gray-600 dark:text-gray-400">
                        <XMarkIcon className="h-4 w-4 text-red-500 mr-1 mt-0.5 flex-shrink-0" />
                        {con}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );

  // Step 3: Business Details
  const renderBusinessDetails = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
        {t('businessFormation:businessDetails.title', 'Business Details')}
      </h2>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('businessFormation:businessDetails.businessName', 'Business Name')} *
        </label>
        <input
          type="text"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          placeholder={t('businessFormation:businessDetails.businessNamePlaceholder', 'Enter your business name')}
          className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {t(
            'businessFormation:businessDetails.businessNameHint',
            'The name must be unique and typically end with LLC, Inc., or Corp depending on your business type.'
          )}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('businessFormation:businessDetails.businessPurpose', 'Business Purpose')} *
        </label>
        <textarea
          value={businessPurpose}
          onChange={(e) => setBusinessPurpose(e.target.value)}
          placeholder={t(
            'businessFormation:businessDetails.businessPurposePlaceholder',
            'Describe the main activities of your business'
          )}
          rows={3}
          className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('businessFormation:businessDetails.principalAddress', 'Principal Business Address')} *
        </label>
        <textarea
          value={principalAddress}
          onChange={(e) => setPrincipalAddress(e.target.value)}
          placeholder={t('businessFormation:businessDetails.addressPlaceholder', 'Street, City, State, ZIP')}
          rows={2}
          className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('businessFormation:businessDetails.registeredAgent', 'Registered Agent Name')}
          </label>
          <input
            type="text"
            value={registeredAgentName}
            onChange={(e) => setRegisteredAgentName(e.target.value)}
            placeholder={t('businessFormation:businessDetails.registeredAgentPlaceholder', 'Name of registered agent')}
            className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('businessFormation:businessDetails.registeredAgentAddress', 'Registered Agent Address')}
          </label>
          <input
            type="text"
            value={registeredAgentAddress}
            onChange={(e) => setRegisteredAgentAddress(e.target.value)}
            placeholder={t('businessFormation:businessDetails.registeredAgentAddressPlaceholder', 'Address in state')}
            className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
          />
        </div>
      </div>

      {selectedBusinessType === 'llc' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('businessFormation:businessDetails.managementType', 'Management Type')}
          </label>
          <select
            value={managementType}
            onChange={(e) => setManagementType(e.target.value)}
            className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
          >
            <option value="member">
              {t('businessFormation:businessDetails.memberManaged', 'Member-Managed (members run the business)')}
            </option>
            <option value="manager">
              {t(
                'businessFormation:businessDetails.managerManaged',
                'Manager-Managed (designated managers run the business)'
              )}
            </option>
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('businessFormation:businessDetails.fiscalYearEnd', 'Fiscal Year End')}
        </label>
        <select
          value={fiscalYearEnd}
          onChange={(e) => setFiscalYearEnd(e.target.value)}
          className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
        >
          <option value="December 31">December 31 (Calendar Year)</option>
          <option value="March 31">March 31</option>
          <option value="June 30">June 30</option>
          <option value="September 30">September 30</option>
        </select>
      </div>
    </div>
  );

  // Step 4: Owner Information
  const renderOwnerInfo = () => {
    const roleOptions =
      selectedBusinessType === 'llc'
        ? ['Member', 'Manager', 'Managing Member']
        : ['Shareholder', 'Director', 'Officer', 'President', 'Secretary', 'Treasurer'];

    return (
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {t(
            'businessFormation:ownerInfo.title',
            selectedBusinessType === 'llc' ? 'Members/Owners Information' : 'Shareholders/Officers Information'
          )}
        </h2>

        {owners.map((owner, index) => (
          <div
            key={index}
            className="p-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium text-gray-900 dark:text-gray-100">
                {t('businessFormation:ownerInfo.owner', 'Owner')} #{index + 1}
              </h3>
              {owners.length > 1 && (
                <button
                  onClick={() => removeOwner(index)}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  {t('businessFormation:ownerInfo.remove', 'Remove')}
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('businessFormation:ownerInfo.name', 'Full Legal Name')}
                </label>
                <input
                  type="text"
                  value={owner.name}
                  onChange={(e) => updateOwner(index, 'name', e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('businessFormation:ownerInfo.ownership', 'Ownership %')}
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={owner.ownership_percentage}
                  onChange={(e) => updateOwner(index, 'ownership_percentage', Number(e.target.value))}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('businessFormation:ownerInfo.role', 'Role')}
                </label>
                <select
                  value={owner.role}
                  onChange={(e) => updateOwner(index, 'role', e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  {roleOptions.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        ))}

        <button
          onClick={addOwner}
          className="w-full py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded-lg hover:border-emerald-500 hover:text-emerald-500 transition-colors"
        >
          + {t('businessFormation:ownerInfo.addOwner', 'Add Another Owner')}
        </button>

        <div className="text-sm text-gray-600 dark:text-gray-400">
          {t('businessFormation:ownerInfo.totalOwnership', 'Total Ownership')}:{' '}
          <span
            className={`font-medium ${
              owners.reduce((sum, o) => sum + o.ownership_percentage, 0) === 100
                ? 'text-green-600'
                : 'text-red-600'
            }`}
          >
            {owners.reduce((sum, o) => sum + o.ownership_percentage, 0)}%
          </span>
          <span className="text-gray-500"> / 100%</span>
        </div>
      </div>
    );
  };

  // Step 5: Review Requirements
  const renderRequirements = () => {
    if (!requirements) return <div className="text-center py-8">{t('common:loading', 'Loading...')}</div>;

    const req = requirements.requirements;

    return (
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {t('businessFormation:requirements.title', 'Formation Requirements')}
        </h2>

        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-emerald-800 dark:text-emerald-200 mb-2">
            {requirements.business_type_name} in {requirements.region_name}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-gray-600 dark:text-gray-400">
                {t('businessFormation:requirements.filingFee', 'Filing Fee')}
              </div>
              <div className="font-semibold text-emerald-700 dark:text-emerald-300">
                ${req.filing_fee?.[selectedBusinessType] || 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-gray-600 dark:text-gray-400">
                {t('businessFormation:requirements.annualFee', 'Annual Fee')}
              </div>
              <div className="font-semibold text-emerald-700 dark:text-emerald-300">
                ${req.annual_fee?.[selectedBusinessType] || 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-gray-600 dark:text-gray-400">
                {t('businessFormation:requirements.processingTime', 'Processing Time')}
              </div>
              <div className="font-semibold text-emerald-700 dark:text-emerald-300">
                {req.processing_time || 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-gray-600 dark:text-gray-400">
                {t('businessFormation:requirements.stateIncomeTax', 'State Income Tax')}
              </div>
              <div className="font-semibold text-emerald-700 dark:text-emerald-300">
                {req.state_income_tax ? t('common:yes', 'Yes') : t('common:no', 'No')}
              </div>
            </div>
          </div>
        </div>

        {req.notes && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex">
              <InformationCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2 flex-shrink-0" />
              <p className="text-sm text-blue-700 dark:text-blue-300">{req.notes}</p>
            </div>
          </div>
        )}

        {req.benefits && req.benefits.length > 0 && (
          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
              {t('businessFormation:requirements.benefits', 'State Benefits')}
            </h4>
            <ul className="space-y-1">
              {req.benefits.map((benefit: string, idx: number) => (
                <li key={idx} className="flex items-start text-sm text-gray-600 dark:text-gray-400">
                  <CheckIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  {benefit}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
            {t('businessFormation:requirements.checklist', 'Document Checklist')}
          </h3>

          {Object.entries(checklist).map(([category, items]) => (
            <div key={category} className="mb-6">
              <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-2 capitalize">
                {category.replace(/_/g, ' ')}
              </h4>
              <div className="space-y-2">
                {items.map((item, idx) => (
                  <div
                    key={idx}
                    className={`flex items-start p-3 rounded-lg border ${
                      item.completed
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <button
                      onClick={() => toggleChecklistItem(category, idx)}
                      className={`flex-shrink-0 w-5 h-5 rounded border-2 mr-3 mt-0.5 flex items-center justify-center ${
                        item.completed
                          ? 'bg-green-500 border-green-500 text-white'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      {item.completed && <CheckIcon className="h-3 w-3" />}
                    </button>
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-2">
                        <span
                          className={`font-medium ${
                            item.completed
                              ? 'text-green-700 dark:text-green-300 line-through'
                              : 'text-gray-900 dark:text-gray-100'
                          }`}
                        >
                          {item.name}
                        </span>
                        {item.required && (
                          <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded">
                            {t('businessFormation:requirements.required', 'Required')}
                          </span>
                        )}
                        {(item.url || item.state_url) && (
                          <a
                            href={item.state_url || item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 flex items-center"
                          >
                            <LinkIcon className="h-3 w-3 mr-1" />
                            {item.state_url ? t('common:buttons.stateInfo', 'State Info') : t('common:buttons.moreInfo', 'More Info')}
                          </a>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Step 6: Generated Documents
  const renderGeneratedDocuments = () => (
    <div className="space-y-6">
      {/* Header with Download All Package */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {t('businessFormation:documents.title', 'Generated Formation Documents')}
        </h2>
        <div className="flex items-center gap-2">
          <select
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value as 'pdf' | 'docx' | 'txt')}
            className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
          >
            <option value="pdf">PDF</option>
            <option value="docx">Word (.docx)</option>
            <option value="txt">Text (.txt)</option>
          </select>
          <button
            onClick={handleDownloadPackage}
            disabled={generatedDocuments.filter((d) => d.content).length === 0}
            className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:bg-gray-400"
          >
            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
            {t('businessFormation:documents.downloadAll', 'Download All')}
          </button>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <div className="flex">
          <InformationCircleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 mr-2 flex-shrink-0" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            {t(
              'businessFormation:documents.disclaimer',
              'These documents are generated templates and should be reviewed by a legal professional before filing. Laws vary by jurisdiction and may have changed since this content was created.'
            )}
          </p>
        </div>
      </div>

      {/* Document Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {generatedDocuments.map((doc) => (
          <div
            key={doc.document_type}
            className={`bg-white dark:bg-gray-800 border rounded-lg p-4 ${
              doc.content
                ? 'border-green-200 dark:border-green-800'
                : doc.requires_additional_info
                ? 'border-amber-200 dark:border-amber-800'
                : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">{doc.document_name}</h3>
                {doc.content && (
                  <span className="text-xs text-green-600 dark:text-green-400 flex items-center mt-1">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    {t('businessFormation:documents.generated', 'Generated')}
                  </span>
                )}
                {doc.requires_additional_info && !doc.content && (
                  <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center mt-1">
                    <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                    {t('businessFormation:documents.needsInfo', 'Needs additional info')}
                  </span>
                )}
                {doc.error && (
                  <span className="text-xs text-red-600 dark:text-red-400 flex items-center mt-1">
                    <XMarkIcon className="h-4 w-4 mr-1" />
                    {t('businessFormation:documents.error', 'Error generating')}
                  </span>
                )}
              </div>
              <DocumentTextIcon className="h-8 w-8 text-gray-400" />
            </div>

            <div className="flex flex-wrap gap-2">
              {doc.content ? (
                <>
                  <button
                    onClick={() => {
                      setSelectedDocument(doc);
                      setShowDocumentPreview(true);
                    }}
                    className="flex items-center px-3 py-1.5 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50"
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    {t('common:buttons.preview', 'Preview')}
                  </button>
                  <ExportDropdownMenu
                    onExport={(format) => handleExportDocument(doc, format === 'word' ? 'docx' : format === 'text' ? 'txt' : format)}
                    onPrint={() => {
                      setSelectedDocument(doc);
                      setTimeout(() => {
                        if (doc.html_content) {
                          const printWindow = window.open('', '_blank');
                          if (printWindow) {
                            printWindow.document.write(doc.html_content);
                            printWindow.document.close();
                            printWindow.print();
                          }
                        } else {
                          window.print();
                        }
                      }, 0);
                    }}
                    showPrint={true}
                  />
                </>
              ) : doc.requires_additional_info ? (
                <button
                  onClick={() => handleOpenAdditionalFieldsModal(doc)}
                  disabled={documentBeingGenerated === doc.document_type}
                  className="flex items-center px-3 py-1.5 text-sm bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-900/50 disabled:opacity-50"
                >
                  {documentBeingGenerated === doc.document_type ? (
                    <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <PlusCircleIcon className="h-4 w-4 mr-1" />
                  )}
                  {t('businessFormation:documents.provideInfo', 'Provide Info & Generate')}
                </button>
              ) : (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {t('businessFormation:documents.generating', 'Generating...')}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Official State Filing Office Link */}
      {stateWebsite && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-3 flex items-center">
            <LinkIcon className="h-5 w-5 mr-2" />
            {t('businessFormation:documents.officialTemplates', 'Official State Templates & Forms')}
          </h3>

          <div className="p-3 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium text-blue-800 dark:text-blue-200 block">
                  {filingOffice || t('businessFormation:filing.filingOffice', 'Filing Office')}
                </span>
                <span className="text-sm text-blue-600 dark:text-blue-400">
                  {t('businessFormation:documents.officialFormsNote', 'Visit the official state website for required forms and templates')}
                </span>
              </div>
              <a
                href={stateWebsite}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center flex-shrink-0"
              >
                {t('businessFormation:filing.visitWebsite', 'Visit Website')}
                <LinkIcon className="h-4 w-4 ml-1" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {showDocumentPreview && selectedDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                {selectedDocument.document_name}
              </h3>
              <div className="flex items-center gap-2">
                <ExportDropdownMenu
                  onExport={(format) => handleExportDocument(selectedDocument, format === 'word' ? 'docx' : format === 'text' ? 'txt' : format)}
                  onPrint={handlePrint}
                  showPrint={true}
                />
                <button
                  onClick={() => {
                    setShowDocumentPreview(false);
                    setSelectedDocument(null);
                  }}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {selectedDocument.html_content ? (
                <div
                  className="prose dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: selectedDocument.html_content }}
                />
              ) : (
                <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-mono">
                  {selectedDocument.content}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Additional Fields Modal */}
      {showAdditionalFieldsModal && selectedDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                {t('businessFormation:documents.additionalInfo', 'Additional Information Required')}
              </h3>
              <button
                onClick={() => {
                  setShowAdditionalFieldsModal(false);
                  setSelectedDocument(null);
                  setAdditionalFieldsValues({});
                }}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t(
                  'businessFormation:documents.additionalInfoDesc',
                  `Please provide the following information to generate the ${selectedDocument.document_name}:`
                )}
              </p>
              {selectedDocument.additional_fields.map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {field.type === 'date' ? (
                    <input
                      type="date"
                      value={additionalFieldsValues[field.name] || ''}
                      onChange={(e) =>
                        setAdditionalFieldsValues((prev) => ({ ...prev, [field.name]: e.target.value }))
                      }
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
                    />
                  ) : field.type === 'number' ? (
                    <input
                      type="number"
                      value={additionalFieldsValues[field.name] || ''}
                      onChange={(e) =>
                        setAdditionalFieldsValues((prev) => ({ ...prev, [field.name]: e.target.value }))
                      }
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
                    />
                  ) : (
                    <input
                      type="text"
                      value={additionalFieldsValues[field.name] || ''}
                      onChange={(e) =>
                        setAdditionalFieldsValues((prev) => ({ ...prev, [field.name]: e.target.value }))
                      }
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowAdditionalFieldsModal(false);
                  setSelectedDocument(null);
                  setAdditionalFieldsValues({});
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                {t('common:buttons.cancel', 'Cancel')}
              </button>
              <button
                onClick={() => generateSingleDocument(selectedDocument.document_type, additionalFieldsValues)}
                disabled={documentBeingGenerated !== null}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400"
              >
                {documentBeingGenerated ? (
                  <span className="flex items-center">
                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                    {t('businessFormation:documents.generating', 'Generating...')}
                  </span>
                ) : (
                  t('businessFormation:documents.generate', 'Generate Document')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Step 7: Filing Instructions
  const renderFilingInstructions = () => {
    if (!filingInstructions) return <div className="text-center py-8">{t('common:loading', 'Loading...')}</div>;

    return (
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {t('businessFormation:filing.title', 'Filing Instructions')}
        </h2>

        {/* Filing Office Info Card */}
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4">
          <h3 className="font-semibold text-emerald-800 dark:text-emerald-200 mb-2">
            {filingInstructions.state} - {filingInstructions.business_type_name}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-gray-600 dark:text-gray-400">
                {t('businessFormation:filing.filingOffice', 'Filing Office')}
              </div>
              <div className="font-medium text-gray-900 dark:text-gray-100">
                {filingInstructions.filing_office}
              </div>
            </div>
            <div>
              <div className="text-gray-600 dark:text-gray-400">
                {t('businessFormation:filing.totalCost', 'Est. Total Cost')}
              </div>
              <div className="font-semibold text-emerald-700 dark:text-emerald-300">
                ${filingInstructions.filing_fee + (filingInstructions.publication_fee || 0)}
              </div>
            </div>
            <div>
              <div className="text-gray-600 dark:text-gray-400">
                {t('businessFormation:filing.processingTime', 'Processing Time')}
              </div>
              <div className="font-medium text-gray-900 dark:text-gray-100">
                {filingInstructions.processing_time}
              </div>
            </div>
            {filingInstructions.website && (
              <div>
                <div className="text-gray-600 dark:text-gray-400">
                  {t('businessFormation:filing.website', 'Website')}
                </div>
                <a
                  href={filingInstructions.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline text-sm"
                >
                  {t('businessFormation:filing.visitWebsite', 'Visit Website')}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Contact Information Panel */}
        {(filingInstructions.office_address || filingInstructions.office_phone || filingInstructions.online_filing) && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-3 flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 mr-2" />
              {t('businessFormation:filing.contactInfo', 'Filing Office Contact Information')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              {filingInstructions.online_filing && (
                <div>
                  <div className="text-blue-600 dark:text-blue-400 font-medium mb-1">
                    {t('businessFormation:filing.onlineFiling', 'Online Filing')}
                  </div>
                  <a
                    href={filingInstructions.online_filing}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 dark:text-blue-300 hover:underline flex items-center"
                  >
                    <LinkIcon className="h-4 w-4 mr-1" />
                    {t('businessFormation:filing.fileOnline', 'File Online')}
                  </a>
                </div>
              )}
              {filingInstructions.office_address && (
                <div>
                  <div className="text-blue-600 dark:text-blue-400 font-medium mb-1">
                    {t('businessFormation:filing.address', 'Address')}
                  </div>
                  <div className="text-gray-700 dark:text-gray-300">
                    {filingInstructions.office_address}
                  </div>
                </div>
              )}
              {filingInstructions.office_phone && (
                <div>
                  <div className="text-blue-600 dark:text-blue-400 font-medium mb-1">
                    {t('businessFormation:filing.phone', 'Phone')}
                  </div>
                  <a
                    href={`tel:${filingInstructions.office_phone.replace(/[^0-9+]/g, '')}`}
                    className="text-gray-700 dark:text-gray-300 hover:underline"
                  >
                    {filingInstructions.office_phone}
                  </a>
                </div>
              )}
            </div>
            {(filingInstructions.name_search || filingInstructions.forms_page) && (
              <div className="mt-4 pt-3 border-t border-blue-200 dark:border-blue-700 flex flex-wrap gap-3">
                {filingInstructions.name_search && (
                  <a
                    href={filingInstructions.name_search}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-lg text-sm hover:bg-blue-200 dark:hover:bg-blue-900/60 flex items-center"
                  >
                    <LinkIcon className="h-4 w-4 mr-1" />
                    {t('businessFormation:filing.searchNames', 'Search Business Names')}
                  </a>
                )}
                {filingInstructions.forms_page && (
                  <a
                    href={filingInstructions.forms_page}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-lg text-sm hover:bg-blue-200 dark:hover:bg-blue-900/60 flex items-center"
                  >
                    <DocumentTextIcon className="h-4 w-4 mr-1" />
                    {t('businessFormation:filing.viewForms', 'View Official Forms')}
                  </a>
                )}
              </div>
            )}
          </div>
        )}

        <div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
            {t('businessFormation:filing.steps', 'Step-by-Step Filing Instructions')}
          </h3>
          <div className="space-y-4">
            {filingInstructions.steps.map((step: FilingStep, idx: number) => (
              <div
                key={idx}
                className="flex bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
              >
                <div className="flex-shrink-0 w-16 bg-emerald-600 flex items-center justify-center text-white font-bold text-xl">
                  {Math.floor(step.step)}
                </div>
                <div className="p-4 flex-1">
                  <div className="flex items-start justify-between">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100">{step.title}</h4>
                    {step.url && step.link_text && (
                      <a
                        href={step.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs hover:bg-blue-200 dark:hover:bg-blue-900/50 flex items-center flex-shrink-0"
                      >
                        <LinkIcon className="h-3 w-3 mr-1" />
                        {step.link_text}
                      </a>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{step.description}</p>
                  <div className="flex gap-4 mt-2 text-xs">
                    <span className="flex items-center text-gray-500 dark:text-gray-400">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {step.estimated_time}
                    </span>
                    <span className="flex items-center text-gray-500 dark:text-gray-400">
                      <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                      {step.cost}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {filingInstructions.publication_required && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">
              {t('businessFormation:filing.publicationRequired', 'Publication Required')}
            </h4>
            <p className="text-sm text-amber-700 dark:text-amber-300">
              {filingInstructions.publication_notes}
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              {t('businessFormation:filing.estimatedCost', 'Estimated Cost')}: ${filingInstructions.publication_fee}
            </p>
          </div>
        )}

        <div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t('businessFormation:filing.annualRequirements', 'Annual Requirements')}
          </h3>
          <ul className="space-y-1">
            {filingInstructions.annual_requirements?.map((req: string, idx: number) => (
              <li key={idx} className="flex items-start text-sm text-gray-600 dark:text-gray-400">
                <CheckCircleIcon className="h-4 w-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                {req}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderJurisdictionSelection();
      case 1:
        return renderBusinessTypeSelection();
      case 2:
        return renderBusinessDetails();
      case 3:
        return renderOwnerInfo();
      case 4:
        return renderRequirements();
      case 5:
        return renderGeneratedDocuments();
      case 6:
        return renderFilingInstructions();
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center mb-4">
            <BuildingOfficeIcon className="h-12 w-12 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {t('businessFormation:title', 'Business Formation')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t(
              'businessFormation:subtitle',
              'Create your LLC, Corporation, or other business entity with guided steps'
            )}
          </p>
        </div>

        {/* Step Indicator */}
        {renderStepIndicator()}

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* Main Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">{renderStepContent()}</div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <button
            onClick={handlePrevStep}
            disabled={currentStep === 0}
            className={`flex items-center px-6 py-3 rounded-lg font-medium transition-colors ${
              currentStep === 0
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            <ChevronLeftIcon className="h-5 w-5 mr-1" />
            {t('common:buttons.back', 'Back')}
          </button>

          {currentStep < STEPS.length - 1 ? (
            <button
              onClick={handleNextStep}
              disabled={isLoading}
              className="flex items-center px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors disabled:bg-gray-400"
            >
              {isLoading ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 mr-1 animate-spin" />
                  {t('common:loading', 'Loading...')}
                </>
              ) : (
                <>
                  {t('common:buttons.next', 'Next')}
                  <ChevronRightIcon className="h-5 w-5 ml-1" />
                </>
              )}
            </button>
          ) : (
            <button
              onClick={resetForm}
              className="flex items-center px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
            >
              <CheckCircleIcon className="h-5 w-5 mr-2" />
              {t('common:buttons.done', 'Done')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
